import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Send, MessageCircle } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "account_help", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);   // { email_delivered }
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    api.get("/contact/topics").then(r => setTopics(r.data.topics || [])).catch(() => setTopics([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (loading || sent) return;              // no duplicate submits while sending
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent({ email_delivered: !!data.email_delivered });
      toast.success("Thanks — your message was sent to the LIMYA team.");
      setForm({ name: "", email: "", topic: "account_help", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "We couldn't send that just now — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <Toaster position="top-center" />
      <main className="flex-1 max-w-2xl mx-auto px-6 py-16 w-full">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#2E6B57]/15 text-[#245544] text-xs font-semibold tracking-wide uppercase mb-4">
            <MessageCircle size={13} /> We'd love to hear from you
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#222] mb-3" style={{ fontFamily: "Outfit" }}>Get in touch</h1>
          <p className="text-[#222]/60">Need a hand or expecting a reply? Send us a note and we'll get back to you. Product ideas and bugs are best sent through <Link to="/feedback" className="font-semibold text-[#245544] underline decoration-[#E0A83E] decoration-2 underline-offset-2" data-testid="contact-feedback-link">Share Feedback</Link>.</p>
        </div>

        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-8">
          {sent ? (
            <div className="text-center py-8" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-[#2E6B57]/15 grid place-items-center mx-auto mb-4">
                <Mail size={26} className="text-[#245544]" />
              </div>
              <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>Thanks — your message was sent to the LIMYA team.</h2>
              <p className="text-sm text-[#222]/60 mb-4">We'll get back to you as soon as we can.</p>
              {!sent.email_delivered && (
                <p className="text-xs text-[#222321] bg-[#FDF4E3] border border-[#E0A83E]/40 rounded-xl px-3 py-2.5 max-w-sm mx-auto mb-4" data-testid="contact-email-pending">
                  Heads up: our outbound email isn't switched on yet during the beta, so your message is waiting in the LIMYA support queue rather than in an inbox. The team still sees it.
                </p>
              )}
              <button onClick={() => setSent(null)} className="text-sm font-semibold text-[#245544] hover:underline" data-testid="contact-send-another">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Your name</label>
                <input
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-1 w-full px-4 py-2.5 border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Email</label>
                <input
                  type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="mt-1 w-full px-4 py-2.5 border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Topic</label>
                <select
                  value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className="mt-1 w-full px-4 py-2.5 border border-black/10 rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="contact-topic"
                >
                  {topics.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-[#222]/60">Message</label>
                <textarea
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required rows={5}
                  className="mt-1 w-full px-4 py-2.5 border border-black/10 rounded-xl outline-none focus:ring-2 focus:ring-[#2E6B57] resize-none"
                  data-testid="contact-message"
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="btn-lime w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Send size={16} /> {loading ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
